import { createSelector } from "@reduxjs/toolkit";

import type { ITranslation } from "@/queries/useTranslations";
import { type ILanguage, LANGUAGES } from "@/types";
import type { IState } from "./index";

const EMPTY_EDITS_DATA: NonNullable<IState["edits"][string]>["data"] = {};
const EMPTY_EDITS: NonNullable<IState["edits"][string]> = { data: EMPTY_EDITS_DATA };

const editsSelector = (state: IState, version: string) => state.edits[version] ?? EMPTY_EDITS;

const editTextsSelector = createSelector(
    (state: IState, version: string, _id: string) => editsSelector(state, version).data,
    (_state: IState, _version: string, id: string) => id,
    (edits, id) => edits.texts?.[id],
);

const editDocsSelector = createSelector(
    (state: IState, version: string, _id: string) => editsSelector(state, version).data,
    (_state: IState, _version: string, id: string) => id,
    (edits, id) => edits.docs?.[id],
);

const editDefaultContentSelector = createSelector(
    (state: IState, version: string, _id: string) => editsSelector(state, version).data,
    (_state: IState, _version: string, id: string) => id,
    (edits, id) => edits.defaultContent?.[id],
);

export const textsItemSelector = createSelector(
    (_state: IState, _version: string, _id: string, translations: ITranslation) => translations,
    (state: IState, version: string, id: string) => editTextsSelector(state, version, id),
    (_state: IState, _version: string, id: string) => id,
    (translation, edits, id) => {
        return LANGUAGES.reduce(
            (obj, lang) => {
                obj[lang] = edits?.[lang] ?? translation.texts.dict[id]?.[lang];
                return obj;
            },
            {} as { [key in ILanguage]: string },
        );
    },
);

export const docsItemSelector = createSelector(
    (_state: IState, _version: string, _id: string, translations: ITranslation) => translations,
    (state: IState, version: string, id: string) => editDocsSelector(state, version, id),
    (_state: IState, _version: string, id: string) => id,
    (translation, edits, id) => {
        return LANGUAGES.reduce(
            (obj, lang) => {
                obj[lang] = edits?.[lang] ?? translation.docs.dict[id]?.[lang];
                return obj;
            },
            {} as { [key in ILanguage]: string },
        );
    },
);

export const defaultContentItemSelector = createSelector(
    (_state: IState, _version: string, _id: string, translations: ITranslation) => translations,
    (state: IState, version: string, id: string) => editDefaultContentSelector(state, version, id),
    (_state: IState, _version: string, id: string) => id,
    (translation, edits, id) => {
        return LANGUAGES.reduce(
            (obj, lang) => {
                obj[lang] = edits?.[lang] ?? translation.defaultContent.dict[id]?.[lang];
                return obj;
            },
            {} as { [key in ILanguage]: string },
        );
    },
);

export const unsavedChangesSelector = createSelector(
    (state: IState) => state.edits,
    (_state: IState, version: string) => version,
    (edits, version) => {
        if (!edits[version]) {
            return 0;
        }
        const numEdits = Object.values(edits[version].data)
            .map((cat) => Object.keys(cat).length)
            .reduce((acc, val) => acc + val, 0);
        return numEdits;
    },
);

export const anyUnsavedChangesSelector = createSelector(
    (state: IState) => state.edits,
    (edits) => {
        for (const version of Object.values(edits)) {
            if (Object.keys(version.data).length > 0) {
                return true;
            }
        }
        return false;
    },
);
